class StickyHeader extends HTMLElement {
  constructor() {
    super();

    this.scrollThreshold = 8;
    this.scrollDirectionDelta = 4;
    this.headerOffset = 50;
    this.lastScrollY = 0;
    this.activationDistance = 0;
    this.isScrollReady = false;
    this.scrollDownAccumulated = 0;
    this.readyTimeout = null;
    this.onScroll = this.onScroll.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onReady = this.onReady.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.animationMode = this.dataset.headerScrollAnimation || 'pill';
    this.activationMode = this.dataset.headerActivationMode || 'header';
    this.scrollHideGap = Number.parseInt(this.dataset.headerScrollHideGap, 10) || 32;
    this.section = this.dataset.sectionId
      ? document.getElementById(`shopify-section-${this.dataset.sectionId}`)
      : null;
    this.lastScrollY = window.scrollY;

    if (this.animationMode !== 'fade' && !this.hasAttribute('data-transparent-header')) return;

    this.updateActivationDistance();
    this.observeHeroResize();
    this.resetToInitialState();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('resize', this.onResize);

    if (document.readyState === 'complete') {
      requestAnimationFrame(() => requestAnimationFrame(this.onReady));
    } else {
      window.addEventListener('load', this.onReady, { once: true });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('load', this.onReady);
    window.removeEventListener('resize', this.onResize);
    this.heroResizeObserver?.disconnect();
    this.clearReadyTimeout();
    this.clearScrollFadeAnimation?.();
  }

  observeHeroResize() {
    if (this.animationMode !== 'fade' || this.activationMode !== 'hero') return;

    const hero = document.querySelector('.hp-hero-section, .cards-hero-section');

    if (!hero || !('ResizeObserver' in window)) return;

    this.heroResizeObserver = new ResizeObserver(() => {
      this.updateActivationDistance();
      this.setHeaderState();
    });
    this.heroResizeObserver.observe(hero);
  }

  clearReadyTimeout() {
    if (!this.readyTimeout) return;

    window.clearTimeout(this.readyTimeout);
    this.readyTimeout = null;
  }

  getHeaderHeight() {
    const cssHeight = Number.parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
      10
    );

    if (cssHeight > 0) return cssHeight;

    return this.offsetHeight || 72;
  }

  updateActivationDistance() {
    if (this.activationMode === 'hero') {
      const hero = document.querySelector('.hp-hero-section, .cards-hero-section');
      this.activationDistance = hero?.offsetHeight || this.getHeaderHeight() + this.headerOffset;
      return;
    }

    this.activationDistance = this.getHeaderHeight() + this.headerOffset;
  }

  setPinnedPhase(isActive) {
    this.classList.toggle('is-sticky-active', isActive);
    this.section?.classList.toggle('is-sticky-active', isActive);
  }

  resetScrollTracking() {
    this.lastScrollY = window.scrollY;
    this.scrollDownAccumulated = 0;
  }

  resetToInitialState() {
    this.classList.remove('is-scroll-hidden', 'is-sticky-active', 'is-scroll-fade-animating');
    this.section?.classList.remove('is-sticky-active');
    this.resetScrollTracking();
  }

  setScrollHidden(isHidden, { animate = false } = {}) {
    this.clearScrollFadeAnimation?.();

    if (isHidden) {
      this.classList.remove('is-scroll-fade-animating');
      this.classList.add('is-scroll-hidden');
      return;
    }

    if (!animate) {
      this.classList.remove('is-scroll-hidden', 'is-scroll-fade-animating');
      return;
    }

    this.classList.add('is-scroll-fade-animating');

    requestAnimationFrame(() => {
      this.classList.remove('is-scroll-hidden');

      const onTransitionEnd = (event) => {
        if (event.target !== this || event.propertyName !== 'opacity') return;

        this.classList.remove('is-scroll-fade-animating');
        this.removeEventListener('transitionend', onTransitionEnd);
        this.clearScrollFadeAnimation = null;
      };

      this.clearScrollFadeAnimation = () => {
        this.classList.remove('is-scroll-fade-animating');
        this.removeEventListener('transitionend', onTransitionEnd);
        this.clearScrollFadeAnimation = null;
      };

      this.addEventListener('transitionend', onTransitionEnd);
    });
  }

  enterPinnedHidden() {
    this.setScrollHidden(true);
    this.setPinnedPhase(true);
  }

  enableScrollHandling() {
    this.clearReadyTimeout();
    this.updateActivationDistance();
    this.lastScrollY = window.scrollY;
    this.isScrollReady = true;
  }

  onResize() {
    if (this.animationMode !== 'fade') return;

    this.updateActivationDistance();
    this.setHeaderState();
  }

  onReady() {
    requestAnimationFrame(() => {
      this.updateActivationDistance();
      this.resetToInitialState();
      this.setHeaderState();

      this.readyTimeout = window.setTimeout(() => {
        this.enableScrollHandling();
      }, 150);
    });
  }

  onPageShow() {
    this.isScrollReady = false;
    this.clearReadyTimeout();
    this.updateActivationDistance();
    this.resetToInitialState();
    this.setHeaderState();

    this.readyTimeout = window.setTimeout(() => {
      this.enableScrollHandling();
    }, 150);
  }

  onScroll() {
    if (!this.isScrollReady) {
      this.lastScrollY = window.scrollY;

      if (this.animationMode === 'fade') {
        this.resetToInitialState();
      }

      this.classList.toggle('is-scrolled', window.scrollY > this.scrollThreshold);
      return;
    }

    this.setHeaderState();
  }

  applyMenuOpenState(scrollY) {
    this.scrollDownAccumulated = 0;
    this.setScrollHidden(false);
    this.setPinnedPhase(scrollY >= this.activationDistance);
  }

  applyInFlowPhase() {
    this.scrollDownAccumulated = 0;
    this.setScrollHidden(false);
    this.setPinnedPhase(false);
  }

  applyPinnedPhase(scrollY) {
    const enteringPinned = this.lastScrollY < this.activationDistance;

    if (enteringPinned) {
      this.scrollDownAccumulated = 0;
      this.enterPinnedHidden();
      return;
    }

    this.setPinnedPhase(true);

    const delta = scrollY - this.lastScrollY;

    if (delta < -this.scrollDirectionDelta) {
      this.scrollDownAccumulated = 0;
      this.setScrollHidden(false, { animate: true });
      return;
    }

    if (delta > this.scrollDirectionDelta) {
      this.scrollDownAccumulated += delta;

      if (this.scrollDownAccumulated >= this.scrollHideGap) {
        this.setScrollHidden(true);
      }
    }
  }

  setHeaderState() {
    const scrollY = window.scrollY;

    this.classList.toggle('is-scrolled', scrollY > this.scrollThreshold);

    if (this.animationMode !== 'fade') {
      this.lastScrollY = scrollY;
      return;
    }

    if (this.classList.contains('is-menu-open')) {
      this.applyMenuOpenState(scrollY);
      this.lastScrollY = scrollY;
      return;
    }

    if (scrollY < this.activationDistance) {
      this.applyInFlowPhase();
    } else {
      this.applyPinnedPhase(scrollY);
    }

    this.lastScrollY = scrollY;
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
